import { type ContentTypeName, contentTypeNames } from "@playbrain/content-types"
import type { ContractLocale } from "@playbrain/contracts"

export const defaultDashboardContentType: ContentTypeName = "news"
export const defaultDashboardPath = "/dashboard/news"
export const dashboardHomePath = "/dashboard"
export const dashboardUsersPath = "/dashboard/users"
export const dashboardRolesPath = "/dashboard/roles"
export const dashboardEmailPath = "/dashboard/email"
export const dashboardPlaydataNewsletterPath = "/dashboard/playdata-newsletter"
export const dashboardPlaceholderSections = ["tempPage", "goalSetting"] as const
// `roles` and `email` are tabs under the admin service rather than launcher
// entries of their own, so they appear here but not in
// `dashboardServiceSections`. `playdataNewsletter` is the same arrangement one
// service over: a tab beside the PLAYDATA content types, not a launcher card,
// because a subscriber list is part of running that site rather than a
// destination anyone opens the dashboard to reach.
export const dashboardUtilitySections = [
  "contractMaker",
  ...dashboardPlaceholderSections,
  "users",
  "roles",
  "email",
  "playdataNewsletter",
] as const
export const dashboardServiceSections = [
  "playbrainWebsite",
  "playdataWebsite",
  "contractMaker",
  ...dashboardPlaceholderSections,
  "users",
] as const
export type DashboardPlaceholderSection = (typeof dashboardPlaceholderSections)[number]
export type DashboardUtilitySection = (typeof dashboardUtilitySections)[number]
export type DashboardServiceSection = (typeof dashboardServiceSections)[number]
export type DashboardSection = ContentTypeName | DashboardUtilitySection
export type ContractMakerLibraryRoute = {
  categories: string[]
  languages: ContractLocale[]
  page: number
  search: string
  type: "library"
}
export type ContractMakerBuilderRoute = {
  categorySlug: string
  contractLocale: ContractLocale
  templateId: string
  type: "builder"
}
export type ContractMakerAdminRoute = {
  templateId: string | null
  type: "admin"
}
export type ContractMakerRoute =
  | ContractMakerLibraryRoute
  | ContractMakerBuilderRoute
  | ContractMakerAdminRoute
export type ContentSectionListRoute = {
  type: "list"
}
export type ContentSectionEditorRoute = {
  /** Null when the editor is creating a document that has no id yet. */
  documentId: string | null
  type: "editor"
}
export type ContentSectionRoute = ContentSectionListRoute | ContentSectionEditorRoute

export const dashboardContentTypePaths: Record<ContentTypeName, string> = {
  news: "/dashboard/news",
  careers: "/dashboard/careers",
  workProducts: "/dashboard/work-products",
  workMarketing: "/dashboard/work-marketing",
  workTags: "/dashboard/work-tags",
  companyHistory: "/dashboard/company-history",
  playdataNews: "/dashboard/playdata-news",
  playdataPrivacy: "/dashboard/playdata-privacy",
  playdataSettings: "/dashboard/playdata-settings",
  playdataTerms: "/dashboard/playdata-terms",
}

export const dashboardPathSegmentsByContentType: Record<ContentTypeName, string> = {
  news: "news",
  careers: "careers",
  workProducts: "work-products",
  workMarketing: "work-marketing",
  workTags: "work-tags",
  companyHistory: "company-history",
  playdataNews: "playdata-news",
  playdataPrivacy: "playdata-privacy",
  playdataSettings: "playdata-settings",
  playdataTerms: "playdata-terms",
}

export const dashboardPathSegmentsByUtilitySection: Record<DashboardUtilitySection, string> = {
  contractMaker: "contract-maker",
  goalSetting: "goal-setting",
  tempPage: "temp-page",
  users: "users",
  roles: "roles",
  email: "email",
  playdataNewsletter: "playdata-newsletter",
}

export const dashboardUtilitySectionPaths: Record<DashboardUtilitySection, string> = {
  contractMaker: "/dashboard/contract-maker",
  goalSetting: "/dashboard/goal-setting",
  tempPage: "/dashboard/temp-page",
  users: dashboardUsersPath,
  roles: dashboardRolesPath,
  email: dashboardEmailPath,
  playdataNewsletter: dashboardPlaydataNewsletterPath,
}

const contentTypesByDashboardPathSegment = Object.fromEntries(
  Object.entries(dashboardPathSegmentsByContentType).map(([type, segment]) => [
    segment,
    type as ContentTypeName,
  ])
) as Record<string, ContentTypeName>

const utilitySectionsByDashboardPathSegment = {
  ...Object.fromEntries(
    Object.entries(dashboardPathSegmentsByUtilitySection).map(([section, segment]) => [
      segment,
      section as DashboardUtilitySection,
    ])
  ),
  contracts: "contractMaker",
} as Record<string, DashboardUtilitySection>

export function dashboardPathForContentType(type: ContentTypeName): string {
  return dashboardContentTypePaths[type] ?? defaultDashboardPath
}

export function dashboardContentTypeFromPathSegment(segment: string | undefined): ContentTypeName | null {
  if (!segment) return null
  return contentTypesByDashboardPathSegment[segment] ?? null
}

export function dashboardSectionFromPathSegment(segment: string | undefined): DashboardSection | null {
  const contentType = dashboardContentTypeFromPathSegment(segment)
  if (contentType) return contentType
  return segment ? (utilitySectionsByDashboardPathSegment[segment] ?? null) : null
}

export function dashboardPathForSection(section: DashboardSection): string {
  if (isDashboardUtilitySection(section)) return dashboardUtilitySectionPaths[section]
  return dashboardPathForContentType(section)
}

export function isDashboardUtilitySection(section: DashboardSection): section is DashboardUtilitySection {
  return dashboardUtilitySections.includes(section as DashboardUtilitySection)
}

export function isDashboardPlaceholderSection(
  section: DashboardSection
): section is DashboardPlaceholderSection {
  return dashboardPlaceholderSections.includes(section as DashboardPlaceholderSection)
}

export function isDashboardContentSection(section: DashboardSection): section is ContentTypeName {
  return !isDashboardUtilitySection(section)
}

export function dashboardServiceForSection(section: DashboardSection): DashboardServiceSection {
  // Roles and the email catalogue sit beside user management under the same
  // admin service, which is what makes them tabs there rather than launcher
  // entries of their own.
  if (section === "users" || section === "roles" || section === "email") return "users"
  if (section === "playdataNewsletter") return "playdataWebsite"
  if (section === "contractMaker") return "contractMaker"
  if (isDashboardPlaceholderSection(section)) return section
  if (
    section === "playdataNews" ||
    section === "playdataTerms" ||
    section === "playdataPrivacy" ||
    section === "playdataSettings"
  ) {
    return "playdataWebsite"
  }
  return "playbrainWebsite"
}

/**
 * The content types a service's tab row should offer.
 *
 * Derived from `dashboardServiceForSection` rather than listed, so a new
 * content type appears under exactly the service that claims it. Without this
 * the tab row showed every type under every service — PLAYDATA's archive sat
 * among PLAYBRAIN's tabs, and PLAYDATA itself fell through to the placeholder
 * branch and offered a single tab named after the service.
 */
export function contentTypesForService(service: DashboardServiceSection): ContentTypeName[] {
  return contentTypeNames.filter((type) => dashboardServiceForSection(type) === service)
}

/** Whether a service's own pages are content lists, as opposed to a bespoke UI. */
export function isContentService(service: DashboardServiceSection): boolean {
  return contentTypesForService(service).length > 0
}

export function dashboardPathForService(service: DashboardServiceSection): string {
  if (service === "playbrainWebsite") return defaultDashboardPath
  if (service === "playdataWebsite") return dashboardContentTypePaths.playdataNews
  return dashboardPathForSection(service)
}

export function contractMakerLibraryPath({
  categories = [],
  languages = [],
  page = 1,
  search = "",
}: {
  categories?: string[]
  languages?: ContractLocale[]
  page?: number
  search?: string
} = {}): string {
  const queryParts: string[] = []
  const categorySlugs = uniqueNonEmpty(categories.map(slugifyDashboardPathSegment))
  const languageValues = uniqueNonEmpty(languages.filter(isContractLocale))
  const safePage = Number.isFinite(page) && page > 1 ? Math.floor(page) : 1

  /*
   * Carried verbatim, spaces included. The library's search box is a mirror of
   * this parameter, so trimming here — and again where it is read back — meant
   * the space that ended "non " never survived the round trip: the box snapped
   * back a character short and a two-word search could not be typed at all.
   * What the query does with the whitespace is the query's business; the URL's
   * job is to say what was typed.
   */
  if (search) queryParts.push(`q=${encodeURIComponent(search)}`)
  if (categorySlugs.length) queryParts.push(`categories=${categorySlugs.join(",")}`)
  if (languageValues.length) queryParts.push(`languages=${languageValues.join(",")}`)
  if (safePage > 1) queryParts.push(`page=${safePage}`)

  return queryParts.length
    ? `${dashboardUtilitySectionPaths.contractMaker}?${queryParts.join("&")}`
    : dashboardUtilitySectionPaths.contractMaker
}

export function contractMakerTemplatePath({
  category,
  locale,
  templateId,
}: {
  category: string
  locale: ContractLocale
  templateId: string
}): string {
  return `${dashboardUtilitySectionPaths.contractMaker}/template-${locale}/${slugifyDashboardPathSegment(category)}/${encodeURIComponent(templateId)}`
}

export function contractMakerEditorPath(templateId: string | null): string {
  return templateId
    ? `${dashboardUtilitySectionPaths.contractMaker}/templates/${encodeURIComponent(templateId)}/edit`
    : `${dashboardUtilitySectionPaths.contractMaker}/templates/new`
}

/**
 * The content sections' addresses, shaped like Contract Maker's.
 *
 * The editor paths accept the list's serialized filters as `searchStr` and
 * carry them through unchanged, so leaving the editor can land on the exact
 * filtered list the reader came from — the modal editor preserved that state
 * by never leaving the page, and the URL has to hold it now that it does.
 */
export function contentListPath(type: ContentTypeName, searchStr = ""): string {
  return `${dashboardPathForContentType(type)}${normalizeSearchSuffix(searchStr)}`
}

export function contentEditorPath(type: ContentTypeName, documentId: string | null, searchStr = ""): string {
  const sectionPath = dashboardPathForContentType(type)
  const editorPath = documentId
    ? `${sectionPath}/${encodeURIComponent(documentId)}/edit`
    : `${sectionPath}/new`
  return `${editorPath}${normalizeSearchSuffix(searchStr)}`
}

/** Null when the path does not belong to a content section. */
export function contentSectionRouteFromLocation(location: { pathname: string }): ContentSectionRoute | null {
  const pathname = location.pathname.replace(/\/+$/, "")
  const match = /^\/dashboard\/([^/]+)(?:\/(.+))?$/.exec(pathname)
  const contentType = dashboardContentTypeFromPathSegment(match?.[1])
  if (!contentType) return null

  const relativePath = match?.[2] ?? ""
  if (relativePath === "new") return { documentId: null, type: "editor" }

  const editorMatch = /^([^/]+)\/edit$/.exec(relativePath)
  if (editorMatch) {
    const documentId = safeDecodePathSegment(editorMatch[1] ?? "")
    // An undecodable id is an unrecognised suffix, not a crash: the route's
    // beforeLoad redirects the list answer to the canonical address.
    if (documentId === null) return { type: "list" }
    return { documentId, type: "editor" }
  }

  return { type: "list" }
}

/**
 * `decodeURIComponent` that answers null instead of throwing.
 *
 * This parser runs inside `beforeLoad`, the loader, and render bodies, and a
 * hand-edited link with a stray `%` must degrade to "not an editor address"
 * rather than take the page down with a `URIError`.
 */
function safeDecodePathSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment)
  } catch {
    return null
  }
}

function normalizeSearchSuffix(searchStr: string): string {
  if (!searchStr || searchStr === "?") return ""
  return searchStr.startsWith("?") ? searchStr : `?${searchStr}`
}

export function contractMakerRouteFromLocation(location: {
  pathname: string
  search?: string
}): ContractMakerRoute {
  const pathname = location.pathname.replace(/\/+$/, "")
  const contractMakerPath = dashboardUtilitySectionPaths.contractMaker
  const relativePath =
    pathname === contractMakerPath ? "" : pathname.replace(new RegExp(`^${contractMakerPath}/?`), "")

  const builderMatch = /^template-(en|ja)\/([^/]+)\/([^/]+)$/.exec(relativePath)
  if (builderMatch) {
    return {
      categorySlug: decodeURIComponent(builderMatch[2] ?? ""),
      contractLocale: builderMatch[1] as ContractLocale,
      templateId: decodeURIComponent(builderMatch[3] ?? ""),
      type: "builder",
    }
  }

  const editorMatch = /^templates\/([^/]+)\/edit$/.exec(relativePath)
  if (editorMatch) {
    return {
      templateId: decodeURIComponent(editorMatch[1] ?? ""),
      type: "admin",
    }
  }

  if (relativePath === "templates/new") {
    return {
      templateId: null,
      type: "admin",
    }
  }

  const searchParams = new URLSearchParams(location.search?.replace(/^\?/, "") ?? "")
  return {
    categories: splitRouteList(searchParams.get("categories")).map(slugifyDashboardPathSegment),
    languages: splitRouteList(searchParams.get("languages")).filter(isContractLocale),
    page: pageFromSearchParam(searchParams.get("page")),
    search: searchParams.get("q") ?? "",
    type: "library",
  }
}

export function slugifyDashboardPathSegment(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function splitRouteList(value: string | null): string[] {
  if (!value) return []
  return uniqueNonEmpty(value.split(",").map((item) => decodeURIComponent(item).trim()))
}

function uniqueNonEmpty<T extends string>(items: T[]): T[] {
  return Array.from(new Set(items.filter(Boolean)))
}

function isContractLocale(value: string): value is ContractLocale {
  return value === "en" || value === "ja"
}

function pageFromSearchParam(value: string | null): number {
  const page = value ? Number(value) : 1
  return Number.isInteger(page) && page > 0 ? page : 1
}
